'use client'
import { useEffect, useState } from 'react'

import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { PhnxOfficeinputsearch } from '@/components/01-atoms/Officeinputsearch/Officeinputsearch'
import { PhnxRelatedArticles } from '@/components/03-organisms/Relatedarticles/RelatedArticles'

import { RadiusValue } from '../../../lib/googlePlaces'
import { PhnxCard } from '../Card/Card'
import styles from './Featuretabs.module.css'

interface Specialty {
  name: string
  value: string
}

interface Specialist {
  icon: string
  asset?: string
  label: string
  href: string
}

interface Solution {
  icon: string
  label: string
  href: string
  asset?: string
}

interface Description {
  title: string
  content: React.ReactNode
}

interface Tab {
  id: string
  title: string
  specialists: Specialist[]
  solutions: Solution[]
  description: Description
  featureCards?: React.ReactNode
  relatedArticles?: React.ReactNode[]
}

export type FeaturetabsProps = {
  specialty: Specialty[]
  tabs: Tab[]
  content: React.ReactNode
}

export const PhnxFeaturetabs: React.FC<FeaturetabsProps> = ({ specialty, tabs, content }) => {
  const [activeTab, setActiveTab] = useState(0)
  const [relatedArticles, setRelatedArticles] = useState<React.ReactNode[]>([])
  const [selectedSpecialty, setSelectedSpecialty] = useState('')
  const [searchLocation, setSearchLocation] = useState('')
  const [searchRadius, setSearchRadius] = useState<RadiusValue>(100)

  useEffect(() => {
    if (tabs != null) {
      const currentTab = tabs.at(activeTab)
      if (currentTab?.relatedArticles != null) {
        setRelatedArticles(currentTab.relatedArticles)
      }
    }
  }, [activeTab, tabs])

  const handleSpecialtyChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSpecialty(event.target.value)
  }

  const handleLocationSelect = (item: { url?: string; name?: string; title?: string }) => {
    // If the item has a url property, navigate to the office page
    if (item.url != null && item.url !== '') {
      window.location.href = item.url
    } else {
      // Fallback to setting search location for backward compatibility
      setSearchLocation(
        item.name != null && item.name !== ''
          ? item.name
          : item.title != null && item.title !== ''
            ? item.title
            : ''
      )
    }
  }

  const handleSearch = () => {
    let searchUrl = '/locations/search'
    const params = new URLSearchParams()

    if (searchLocation) {
      params.append('location', searchLocation)
    }
    if (selectedSpecialty && selectedSpecialty !== '') {
      params.append('specialty', selectedSpecialty)
    }

    if (params.toString()) {
      searchUrl += `?${params.toString()}`
    }

    window.location.href = searchUrl
  }

  return (
    <div className={styles.featuretabs}>
      <div className={styles.tabs} role="tablist">
        <div className={`container ${styles['tab-container']}`}>
          {tabs != null &&
            tabs.map((tab, index) => (
              <button
                key={index}
                role="tab"
                aria-selected={activeTab === index}
                aria-controls={`tab-panel-${index}`}
                id={`tab-${index}`}
                className={`${activeTab === index ? styles.active : ''}`}
                onClick={() => setActiveTab(index)}
              >
                {tab.title}
              </button>
            ))}
        </div>
      </div>
      <div
        className={`container ${styles['info-container']}`}
        role="tabpanel"
        id={`tab-panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
      >
        <div className={styles['search-adviser']}>
          <h2>Find an adviser near you</h2>

          <div className={styles['search-form']}>
            <PhnxOfficeinputsearch
              variant="secondary"
              placeholder="Enter postcode (4 digits) or suburb"
              onSelect={handleLocationSelect}
              enablePlacesAPI={true}
              searchRadius={searchRadius}
              onRadiusChange={setSearchRadius}
              showRadiusSelector={true}
            />

            <div className={styles.or}>Or</div>

            <label htmlFor="specialty" className="sr-only">
              Select a specialty
            </label>
            <select
              name="specialty"
              id="specialty"
              value={selectedSpecialty}
              onChange={handleSpecialtyChange}
            >
              <option value="">Select a specialty</option>
              {specialty != null &&
                specialty.map((specialtyItem, index) => (
                  <option key={index} value={specialtyItem.value}>
                    {specialtyItem.name}
                  </option>
                ))}
            </select>
          </div>

          <div className={styles['submit-container']}>
            <a href="/offices/search">Or view all offices</a>
            <PhnxButton label="Find" type="button" onClick={handleSearch} />
          </div>
        </div>

        <div className={styles.specialist}>
          <h2>Our specialist industries</h2>
          <ul className={styles.list}>
            {tabs != null &&
              (() => {
                const currentTab = tabs.at(activeTab)
                return currentTab?.specialists?.map((specialist, index) => (
                  <li key={index}>
                    <a href={specialist.href}>
                      <div className="icon">
                        <span className={`fx-icon ${styles.icon} ${styles.right}`}>
                          {specialist.icon && (
                            <span className={`fx-icon ${specialist.icon}`}></span>
                          )}
                          {specialist.asset != null && specialist.asset !== '' && (
                            <img
                              src={specialist.asset}
                              alt={specialist.label}
                              style={{ maxHeight: '50px' }}
                            />
                          )}
                        </span>
                      </div>
                      <h3>{specialist.label}</h3>
                    </a>
                  </li>
                ))
              })()}
          </ul>
        </div>

        <div className={styles.solutions}>
          <h2>Solutions for you</h2>
          <ul className={styles.list}>
            {tabs != null &&
              (() => {
                const currentTab = tabs.at(activeTab)
                return currentTab?.solutions?.map((solution, index) => (
                  <li key={index}>
                    <a href={solution.href}>
                      <div className="icon">
                        <span className={`fx-icon ${styles.icon} ${styles.right}`}>
                          {solution.icon && <span className={`fx-icon ${solution.icon}`}></span>}
                          {solution.asset != null && solution.asset !== '' && (
                            <img
                              src={solution.asset}
                              alt={solution.label}
                              style={{ maxHeight: '50px' }}
                            />
                          )}
                        </span>
                      </div>
                      <h3>{solution.label}</h3>
                    </a>
                  </li>
                ))
              })()}
          </ul>
        </div>
      </div>
      <div className={`${styles['text-container']} container`}>
        {(() => {
          if (tabs == null) return null
          const currentTab = tabs.at(activeTab)
          if (!currentTab?.description) return null

          return (
            <>
              {currentTab.description.title != null && currentTab.description.title !== '' && (
                <h2>{currentTab.description.title}</h2>
              )}
              {currentTab.description.content != null && currentTab.description.content}
            </>
          )
        })()}
      </div>
      {content != null && content}
      {tabs != null && relatedArticles != null && relatedArticles.length > 0 && (
        <div className={styles['related-articles']}>
          <PhnxRelatedArticles
            title={'INSIGHTS'}
            button={
              <PhnxButton type="link" variant="secondary" label="View more" href="/insights" />
            }
            layout={'quinary'}
            backgroundColor={'#F2F5F6'}
            padding={{
              unit: 'rem',
              default: true,
            }}
            container={true}
            items={relatedArticles.map((item: React.ReactNode, index: number) => (
              <PhnxCard
                key={`related-article-${index}`}
                variant={'primary'}
                layout="vertical"
                alignment="default"
                style={'square'}
                title={
                  typeof item === 'object' && item !== null && 'title' in item
                    ? String(item.title)
                    : ''
                }
                description={
                  typeof item === 'object' && item !== null && 'description' in item
                    ? String(item.description)
                    : ''
                }
                tags={
                  typeof item === 'object' && item !== null && 'tags' in item
                    ? (item.tags as string[])
                    : []
                }
                author={
                  typeof item === 'object' && item !== null && 'author' in item
                    ? (item.author as React.ReactNode)
                    : null
                }
                asset={
                  typeof item === 'object' &&
                  item !== null &&
                  'asset' in item &&
                  item.asset != null ? (
                    <PhnxImageHolder
                      images={[
                        {
                          src: (item.asset as { src: string; alt: string; focus: string }).src,
                          alt: (item.asset as { src: string; alt: string; focus: string }).alt,
                          focus: (item.asset as { src: string; alt: string; focus: string }).focus,
                        },
                      ]}
                      maximumHeight={250}
                    />
                  ) : undefined
                }
                link={
                  typeof item === 'object' && item !== null && 'link' in item
                    ? (item.link as string)
                    : undefined
                }
              />
            ))}
          />
        </div>
      )}
      {(() => {
        if (tabs == null) return null
        const currentTab = tabs.at(activeTab)
        if (currentTab?.featureCards == null) return null
        return <div className={styles['feature-cards']}>{currentTab.featureCards}</div>
      })()}
    </div>
  )
}
